import { httpClient } from '@/src/@core/lib/httpClient'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { CalendarColors, EventType, EventsData, calendarIntialState } from '@/src/types/apps/gygCalendarTypes'
import { Redux } from '../..'
import dayjs from 'dayjs'
import { uniqBy } from 'lodash'
import { CLEANING_SCHEDULE_EVENT, EVENTS_DATA, TRADING_HOURS_EVENT } from '../../action'

const initialState: calendarIntialState = {
  events: {},
  selectedEvent: [],
  loading: false,
  error: undefined,
  cleaningSchedule: [],
  allCleaningSchedule: [],
  tradingHours: [],
  allTradingHours: []
}

export const fetchEvents = createAsyncThunk<CalendarColors, void, Redux>(
  EVENTS_DATA,
  async (_, { rejectWithValue }) => {
    try {
      const response = await httpClient('event')
      const calendarsColor = response.data.data

      return calendarsColor
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data)
      }
    }
  }
)

export const fetchTradingHours = createAsyncThunk<
  EventsData[],
  { restoId: number; month: number; year: number },
  Redux
>(TRADING_HOURS_EVENT, async ({ restoId, month, year }, { extra, rejectWithValue }) => {
  try {
    const response = await extra.httpClient(
      `store-section-time-slot-overrides/store/${restoId}?month=${month}&year=${year}`
    )

    const events: EventsData[] = response.data.data.map((event: EventType) => {
      const title =
        event.openingTime === '00:00:00' && event.closingTime === '00:00:00'
          ? `${event.section.name}`
          : `${event.section.name} - ${dayjs(event.openingTime, 'HH:mm:ss').format('hh:mm A')} - ${dayjs(event.closingTime, 'HH:mm:ss').format('hh:mm A')}`

      return {
        title,
        start: event.effectiveFrom,
        end: event.effectiveTo,
        allDay: true,
        extendedProps: {
          calendar: event.openingTime === '00:00:00' && event.closingTime === '00:00:00' ? 'error' : event.event
        },
        uniqBy: `${event.id}-${title}-${event.effectiveFrom}-${event.effectiveTo}`
      }
    })

    return uniqBy(events, 'uniqBy')
  } catch (error: any) {
    if (error.response) {
      return rejectWithValue(error.response.data)
    }

    return rejectWithValue(error.message)
  }
})

export const fetchCleaningSchedule = createAsyncThunk<
  EventsData[],
  { restoId: number; month: number; year: number },
  Redux
>(CLEANING_SCHEDULE_EVENT, async ({ restoId, month, year }, { extra, rejectWithValue }) => {
  try {
    const response = await extra.httpClient(
      `resto365-restaurant-scheduled-tasks/restaurant/${restoId}/monthly-schedule-summary?month=${month}&year=${year}`
    )

    const data = response.data.data as Record<string, number>
    const events: EventsData[] = Object.entries(data).map(([date, taskCount]) => ({
      id: `rss-${date}`,
      title: `Tasks: ${taskCount}`,
      start: date,
      end: date,
      allDay: true,
      extendedProps: {
        calendar: 'cleaning-schedule',
        taskCount: taskCount.toString()
      }
    }))

    return uniqBy(events, 'id')
  } catch (error: any) {
    if (error.response) {
      return rejectWithValue(error.response.data)
    }

    return rejectWithValue(error.message)
  }
})

export const gygCalendarSlice = createSlice({
  name: 'gygCalendar',
  initialState,
  reducers: {
    handleEvent: (state, action) => {
      const calendarName = action.payload
      const isSelected = state.selectedEvent.includes(calendarName)

      if (isSelected) {
        state.selectedEvent = state.selectedEvent.filter(event => event !== calendarName)
      } else {
        state.selectedEvent.push(calendarName)
      }

      state.tradingHours = state.allTradingHours.filter(() => state.selectedEvent.includes('trading hours'))
      state.cleaningSchedule = state.allCleaningSchedule.filter(() => state.selectedEvent.includes('cleaning schedule'))
    },
    handleAllEvent: (state, action) => {
      const value = action.payload

      if (value === true) {
        state.selectedEvent = Object.keys(state.events)
        state.tradingHours = [...state.allTradingHours]
        state.cleaningSchedule = [...state.allCleaningSchedule]
      } else {
        state.selectedEvent = []
        state.tradingHours = []
        state.cleaningSchedule = []
      }
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTradingHours.pending, state => {
        state.loading = true
        state.error = undefined
      })
      .addCase(fetchTradingHours.fulfilled, (state, action) => {
        state.loading = false
        state.tradingHours = action.payload
        state.allTradingHours = action.payload
      })
      .addCase(fetchTradingHours.rejected, (state, error) => {
        const payload: any = error.payload
        state.error = payload ? payload.message : error.error.message
        state.loading = false
      })

      .addCase(fetchCleaningSchedule.pending, state => {
        state.loading = true
        state.error = undefined
      })
      .addCase(fetchCleaningSchedule.fulfilled, (state, action) => {
        state.loading = false
        state.cleaningSchedule = action.payload
        state.allCleaningSchedule = action.payload
      })
      .addCase(fetchCleaningSchedule.rejected, (state, error) => {
        const payload: any = error.payload
        state.error = payload ? payload.message : error.error.message
        state.loading = false
      })

      .addCase(fetchEvents.pending, state => {
        state.loading = true
        state.error = undefined
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.loading = false
        state.events = action.payload
        state.selectedEvent = Object.keys(action.payload)
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
  }
})

export const { handleEvent, handleAllEvent } = gygCalendarSlice.actions

export default gygCalendarSlice.reducer
